/**
 * Client Document Portal client (T-043). Two modes:
 *   - account: a signed-in user — bearer token via authenticatedFetch;
 *   - link: a verified share link — the HttpOnly `ht_portal_link` cookie the
 *     browser sends on same-origin /api/v1/portal requests (never readable here).
 * Access is decided server-side (services/portal/portalAccess.js).
 */
import { authenticatedFetch } from '@/lib/api';
import { currentSupabaseEnv } from '@/lib/supabase';

export type PortalMode = 'account' | 'link';

export interface PortalBookingView {
  rental_id: string;
  phase: 'upcoming' | 'active' | 'wrapped';
  company: { id: string | null; name: string | null };
  show: { id: string | null; name: string | null };
  start_date: string | null;
  /** Display end date — the last billable day. Never adjust by a day. */
  last_billable_date: string | null;
  trailer_order: { model_code: string; label: string; count: number }[];
  slots: {
    slot_id: string;
    model_code: string;
    label: string;
    /** null → "Trailer not yet assigned" */
    unit: { id: string; unit_code: string } | null;
    start_date: string | null;
    last_billable_date: string | null;
    leg_index: number | null;
  }[];
  /** null → "To be confirmed" */
  delivery_point: { display: string | null; city: string | null; state: string | null; access_notes?: string | null } | null;
  purchase_orders?: { id: string; po_number: string | null; customer_po_number: string | null; status: string | null }[];
  quote_number?: string | null;
  /** Staff only: false when the booking's company doesn't use the client portal yet (the Inc. at launch). */
  portal_available?: boolean;
}

export class PortalError extends Error {
  /** `attemptsLeft`: on code_incorrect, how many more tries that code has. */
  constructor(message: string, public status: number, public code?: string, public attemptsLeft?: number) {
    super(message);
    this.name = 'PortalError';
  }
}

async function linkFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('X-Supabase-Environment', currentSupabaseEnv);
  if (init.body) headers.set('Content-Type', 'application/json');
  return fetch(path, { ...init, headers, credentials: 'same-origin' });
}

export async function readError(res: Response): Promise<PortalError> {
  let body: { message?: string; code?: string; attempts_left?: unknown } = {};
  try { body = await res.json(); } catch { /* not JSON */ }
  const attemptsLeft = typeof body.attempts_left === 'number' ? body.attempts_left : undefined;
  return new PortalError(body.message || `Request failed (${res.status})`, res.status, body.code, attemptsLeft);
}

export function portalFetch(mode: PortalMode, path: string, init: RequestInit = {}): Promise<Response> {
  if (mode === 'link') return linkFetch(path, init);
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  return authenticatedFetch(path, { ...init, headers });
}

/**
 * T-229: the share link's booking was merged into another. `merged_into` is set only
 * when this viewer may see that booking too.
 */
export interface PortalMergedNotice {
  kind: 'merged';
  rental_id: string;
  merged_into: { rental_id: string } | null;
}

/** T-199: who sent the share link this viewer opened. A link made by staff names Hollywood Trucks. */
export interface PortalSharedBy {
  name: string | null;
  fromHollywoodTrucks: boolean;
}

/** The bookings, plus any notices about them (kept on the array so callers still get a list). */
export type PortalBookingList = PortalBookingView[] & { notices?: PortalMergedNotice[]; sharedBy?: PortalSharedBy | null };

function readSharedBy(raw: unknown): PortalSharedBy | null {
  if (!raw || typeof raw !== 'object') return null;
  const { name, from_hollywood_trucks: fromHt } = raw as Record<string, unknown>;
  return { name: typeof name === 'string' && name.trim() ? name.trim() : null, fromHollywoodTrucks: fromHt === true };
}

function readNotices(raw: unknown): PortalMergedNotice[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((n): PortalMergedNotice[] => {
    if (!n || typeof n !== 'object') return [];
    const { kind, rental_id: rentalId, merged_into: into } = n as Record<string, unknown>;
    if (kind !== 'merged' || typeof rentalId !== 'string') return [];
    const target = into && typeof into === 'object' && typeof (into as { rental_id?: unknown }).rental_id === 'string'
      ? { rental_id: (into as { rental_id: string }).rental_id }
      : null;
    return [{ kind: 'merged', rental_id: rentalId, merged_into: target }];
  });
}

/**
 * The bookings this viewer may see. Empty when there are none — never fabricated.
 * Staff view one company at a time (`companyId`); every other viewer's scope comes from the server.
 */
export async function getPortalBookings(mode: PortalMode, opts: { companyId?: string | null } = {}): Promise<PortalBookingList> {
  const path = opts.companyId
    ? `/api/v1/portal/bookings?company_id=${encodeURIComponent(opts.companyId)}`
    : '/api/v1/portal/bookings';
  const res = await portalFetch(mode, path);
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { bookings?: PortalBookingView[]; notices?: unknown; shared_by?: unknown };
  const list: PortalBookingList = Array.isArray(body.bookings) ? body.bookings : [];
  list.notices = readNotices(body.notices);
  list.sharedBy = readSharedBy(body.shared_by);
  return list;
}

/**
 * ISS-163: one booking as it is now (GET /bookings/:rentalId), for the paperwork summary, which must
 * not print dates the page loaded earlier. null when the viewer can't see it any more, or it was
 * merged into another booking. Throws on any other failure.
 */
export async function getPortalBooking(mode: PortalMode, rentalId: string): Promise<PortalBookingView | null> {
  const res = await portalFetch(mode, bookingPath(rentalId, ''));
  if (res.status === 404) return null;
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { booking?: PortalBookingView | null };
  return body.booking ?? null;
}

/** One document in the client's "Production Documents" list. */
export interface PortalDocument {
  document_id: string;
  unit_id: string;
  unit_code: string;
  document_type_key: string;
  file_name: string | null;
  file_size_bytes: number;
  /** Derived status: current | expiring_soon | expired | ... */
  status: string;
  /** Whether THIS user has already seen this document (drives the "New" marker). */
  read: boolean;
  /** T-046: the type's name ("Vehicle Registration"); null when the type has none. Booking documents only. */
  label?: string | null;
  /** T-046: "YYYY-MM-DD" or null (no expiry recorded). Booking documents only. */
  expiration_date?: string | null;
}

/** T-046: a required document a trailer on the booking doesn't have yet ("Not on file yet"). */
export interface PortalPendingDocument {
  unit_id: string;
  unit_code: string;
  document_type_key: string;
  label: string;
}

/** The documents on the user's own bookings, each tagged read/unread. Account mode only. */
export async function getPortalDocuments(mode: PortalMode): Promise<{ documents: PortalDocument[]; readsEnabled: boolean }> {
  const res = await portalFetch(mode, '/api/v1/portal/documents');
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { documents?: PortalDocument[]; reads_enabled?: boolean };
  return { documents: Array.isArray(body.documents) ? body.documents : [], readsEnabled: body.reads_enabled !== false };
}

/** The unread "new documents" count for the Downloads nav badge. Never throws to the caller — returns 0 on any failure. */
export async function getPortalDocUnreadCount(mode: PortalMode): Promise<number> {
  try {
    const res = await portalFetch(mode, '/api/v1/portal/documents/unread-count');
    if (!res.ok) return 0;
    const body = await res.json() as { unread?: number };
    return typeof body.unread === 'number' ? body.unread : 0;
  } catch {
    return 0;
  }
}

/**
 * Whether this signed-in viewer can open the client portal (staff, a company member,
 * or someone with a booking grant). Never throws: any failure reads as false.
 */
export async function getPortalAccess(): Promise<boolean> {
  try {
    const res = await portalFetch('account', '/api/v1/portal/access');
    if (!res.ok) return false;
    const body = await res.json() as { can_open?: boolean };
    return body.can_open === true;
  } catch {
    return false;
  }
}

/** Fired after documents are marked read so the nav badge can refetch promptly. */
export const PORTAL_DOCS_READ_EVENT = 'portal-docs-read';

/**
 * Mark documents read for this user (clears them from the badge). Chunked so a
 * large tenant's id list never exceeds the API's 100KB JSON body cap (which would
 * 413 and leave the badge stuck). Emits PORTAL_DOCS_READ_EVENT on success.
 */
export async function markPortalDocumentsRead(mode: PortalMode, documentIds: string[]): Promise<void> {
  if (documentIds.length === 0) return;
  const CHUNK = 400; // ~400 uuids ≈ 15KB, comfortably under the 100KB limit
  for (let i = 0; i < documentIds.length; i += CHUNK) {
    const res = await portalFetch(mode, '/api/v1/portal/documents/read', {
      method: 'POST',
      body: JSON.stringify({ document_ids: documentIds.slice(i, i + CHUNK) }),
    });
    if (!res.ok) throw await readError(res);
  }
  try { window.dispatchEvent(new Event(PORTAL_DOCS_READ_EVENT)); } catch { /* non-DOM env */ }
}

/** Get a short-lived signed URL to download a portal document. */
export async function openPortalDocument(mode: PortalMode, documentId: string): Promise<{ url: string; fileName: string | null }> {
  const res = await portalFetch(mode, `/api/v1/portal/documents/${encodeURIComponent(documentId)}/download`);
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { url: string; fileName?: string | null };
  return { url: body.url, fileName: body.fileName ?? null };
}

/**
 * The vehicle documents for one booking (v2.2 P4): the trailers assigned to it, in account and
 * share-link mode. `read` is tracked for signed-in clients only (a share link reads all as seen).
 */
export async function getBookingDocuments(mode: PortalMode, rentalId: string): Promise<{ documents: PortalDocument[]; pending: PortalPendingDocument[]; readsEnabled: boolean }> {
  const res = await portalFetch(mode, bookingPath(rentalId, '/documents'));
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { documents?: PortalDocument[]; pending?: PortalPendingDocument[]; reads_enabled?: boolean };
  return {
    documents: Array.isArray(body.documents) ? body.documents : [],
    pending: Array.isArray(body.pending) ? body.pending : [],
    readsEnabled: body.reads_enabled === true,
  };
}

/** A signed-in client has seen these documents on the booking (clears their "New" marker and badge). */
export async function markBookingDocumentsRead(mode: PortalMode, rentalId: string, documentIds: string[]): Promise<void> {
  if (documentIds.length === 0) return;
  const CHUNK = 400; // ~400 uuids ≈ 15KB, comfortably under the 100KB limit
  for (let i = 0; i < documentIds.length; i += CHUNK) {
    const res = await portalFetch(mode, bookingPath(rentalId, '/documents/read'), {
      method: 'POST',
      body: JSON.stringify({ document_ids: documentIds.slice(i, i + CHUNK) }),
    });
    if (!res.ok) throw await readError(res);
  }
  try { window.dispatchEvent(new Event(PORTAL_DOCS_READ_EVENT)); } catch { /* non-DOM env */ }
}

/** A short-lived signed URL for one of the booking's documents. */
export async function openBookingDocument(mode: PortalMode, rentalId: string, documentId: string): Promise<{ url: string; fileName: string | null }> {
  const res = await portalFetch(mode, bookingPath(rentalId, `/documents/${encodeURIComponent(documentId)}/download`));
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { url: string; fileName?: string | null };
  return { url: body.url, fileName: body.fileName ?? null };
}

/** A short-lived signed URL to the booking's documents as one zip; `documentIds` zips only those (T-046 "Download selected"). */
export async function downloadAllBookingDocuments(mode: PortalMode, rentalId: string, documentIds?: string[]): Promise<{ url: string; fileName: string | null; count: number }> {
  const init: RequestInit = documentIds?.length
    ? { method: 'POST', body: JSON.stringify({ document_ids: documentIds }) }
    : { method: 'POST' };
  const res = await portalFetch(mode, bookingPath(rentalId, '/documents/bundle'), init);
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { url: string; fileName?: string | null; count?: number };
  return { url: body.url, fileName: body.fileName ?? null, count: Number(body.count) || 0 };
}

/** Billing details on file for a booking (v2.2 P3). Account-gated: a share link gets none. */
export interface PortalBilling {
  billing_entity: string;
  billing_address: string;
  job_number: string | null;
  received_at: string | null;
  /** Who sent them (name or email), when known. */
  sent_by: string | null;
}

/**
 * Why billing can't be sent from here: a share link confirms the viewer's email first ('verify');
 * an older server asked a share link to sign in ('sign_in'); a signed-in viewer whose
 * membership doesn't cover the booking can't (their production's account does); staff use Sales;
 * 'unavailable' when the booking has no company yet. null when the viewer can send.
 */
export type BillingReadOnlyReason = 'sign_in' | 'verify' | 'not_covered' | 'staff' | 'unavailable' | null;

export async function getBookingBilling(mode: PortalMode, rentalId: string): Promise<{ billing: PortalBilling | null; canEdit: boolean; readOnlyReason: BillingReadOnlyReason }> {
  const res = await portalFetch(mode, `/api/v1/portal/bookings/${encodeURIComponent(rentalId)}/billing`);
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { billing?: PortalBilling | null; can_edit?: boolean; read_only_reason?: BillingReadOnlyReason };
  const canEdit = body.can_edit === true;
  // An older server sent no reason; read that as "sign in" only for a viewer who can't edit.
  return { billing: body.billing ?? null, canEdit, readOnlyReason: canEdit ? null : (body.read_only_reason ?? 'sign_in') };
}

/** Send or update the booking's billing details. The latest submission wins. */
export async function sendBookingBilling(
  mode: PortalMode,
  rentalId: string,
  fields: { billing_entity: string; billing_address: string; job_number?: string },
): Promise<{ receivedAt: string | null }> {
  const res = await portalFetch(mode, `/api/v1/portal/bookings/${encodeURIComponent(rentalId)}/billing`, {
    method: 'POST',
    body: JSON.stringify(fields),
  });
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { received_at?: string | null };
  return { receivedAt: body.received_at ?? null };
}

/**
 * Staff only: create a share link for a booking (opens it without signing in). The URL holds the
 * only copy of the token, so it is shown once and never stored.
 */
export async function createPortalShareLink(rentalId: string): Promise<{ url: string; expiresAt: string | null }> {
  const res = await authenticatedFetch('/api/v1/admin/portal/links', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rental_id: rentalId }),
  });
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { url: string; link?: { expires_at?: string | null } };
  return { url: body.url, expiresAt: body.link?.expires_at ?? null };
}

/** T-207: what was done with a share link, from the audit trail. */
export interface PortalShareLinkActivity {
  first_opened_at: string | null;
  last_activity_at: string | null;
  /** The link logs its first open, then one a day. */
  days_opened: number;
  downloads: number;
  edits: number;
  verified: boolean;
  shared_onward: number;
}

export interface PortalShareLinkEvent {
  at: string;
  kind: 'sent' | 'opened' | 'revoked' | 'download' | 'verified' | 'edit' | 'shared' | 'other';
  text: string;
}

/** A booking's share link as staff see it (never the token). */
export interface PortalShareLink {
  id: string;
  label: string | null;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  use_count: number;
  /** T-207 "Shared with": who sent it, to whom (masked) and how. Null on links made before senders were recorded. */
  sent_by_kind?: 'staff' | 'account' | 'contact' | null;
  sent_by_name?: string | null;
  sent_to?: string | null;
  sent_via?: 'email' | 'copy' | 'native_share' | null;
  /** Null when the trail couldn't be read. */
  activity?: PortalShareLinkActivity | null;
  /** The latest events, newest first. */
  events?: PortalShareLinkEvent[];
}

/** Staff: the booking's share links, newest first. */
export async function listPortalShareLinks(rentalId: string): Promise<PortalShareLink[]> {
  const res = await authenticatedFetch(`/api/v1/admin/portal/links?rental_id=${encodeURIComponent(rentalId)}`);
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { links?: PortalShareLink[] };
  return Array.isArray(body.links) ? body.links : [];
}

/** Staff: revoke a share link. It stops working on the next request (the gate re-checks). */
export async function revokePortalShareLink(id: string): Promise<{ id: string; revoked_at: string | null }> {
  const res = await authenticatedFetch(`/api/v1/admin/portal/links/${encodeURIComponent(id)}/revoke`, { method: 'POST' });
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { link: { id: string; revoked_at: string | null } };
  return body.link;
}

/** Trade a share-link token for the portal cookie. Returns the now-active booking id. */
export async function exchangePortalLink(token: string): Promise<{ rental_id: string; expires_at: string }> {
  const res = await linkFetch('/api/v1/portal/links/exchange', { method: 'POST', body: JSON.stringify({ token }) });
  if (!res.ok) throw await readError(res);
  return res.json();
}

/** Forget the share link on this device. Rejects unless the server confirmed the cookie is cleared. */
export async function leavePortalLink(): Promise<void> {
  const res = await linkFetch('/api/v1/portal/links/leave', { method: 'POST' });
  if (!res.ok) throw await readError(res);
}

type TokenWindow = { __htPortalLinkToken?: string };

/** The token the early inline script moved out of the address bar, if any. */
export function readStashedLinkToken(): string | null {
  return (window as unknown as TokenWindow).__htPortalLinkToken ?? null;
}

/** Drop the stashed token once it has been exchanged (or rejected). */
export function clearStashedLinkToken(): void {
  delete (window as unknown as TokenWindow).__htPortalLinkToken;
}

/** A change request as Sales records it (T-044). The booking itself never changes here. */
export interface PortalChangeRequest {
  id: string;
  status: 'open' | 'accepted' | 'declined';
  requested_start_date: string | null;
  /** Inclusive last day, like last_billable_date. */
  requested_end_date: string | null;
  requested_delivery_point: string | null;
  notes: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  /** The rep's note to the client. */
  decision_note: string | null;
  /**
   * When Hollywood Trucks applied an accepted request to the booking (Sales ISS-252). Absent
   * until Sales sends it; an accepted request without it still reads Accepted.
   */
  applied_at?: string | null;
}

/**
 * What the client asks to change. A share-link viewer no longer types an email here: Hollywood
 * Trucks knows who sent it from the confirmed email (T-195 verification).
 */
export interface ChangeRequestInput {
  requested_start_date?: string;
  requested_end_date?: string;
  requested_delivery_point?: string;
  notes?: string;
}

export const bookingPath = (rentalId: string, rest: string) => `/api/v1/portal/bookings/${encodeURIComponent(rentalId)}${rest}`;

export async function listChangeRequests(mode: PortalMode, rentalId: string): Promise<PortalChangeRequest[]> {
  const res = await portalFetch(mode, bookingPath(rentalId, '/change-requests'));
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { change_requests?: PortalChangeRequest[] };
  return Array.isArray(body.change_requests) ? body.change_requests : [];
}

/** Send a change request. Reuse `idempotencyKey` when retrying the same attempt. */
export async function submitChangeRequest(
  mode: PortalMode, rentalId: string, input: ChangeRequestInput, idempotencyKey: string,
): Promise<PortalChangeRequest | null> {
  const res = await portalFetch(mode, bookingPath(rentalId, '/change-requests'), {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { change_request?: PortalChangeRequest | null };
  return body.change_request ?? null;
}

/** A fresh, short-lived link to the PO in Sales (account mode only). The url carries a token: open it, never store it. */
export async function openPoLink(rentalId: string, purchaseOrderId: string): Promise<{ url: string; expires_at: string | null }> {
  const res = await portalFetch('account', bookingPath(rentalId, '/po-link'), {
    method: 'POST',
    body: JSON.stringify({ purchase_order_id: purchaseOrderId }),
  });
  if (!res.ok) throw await readError(res);
  return res.json();
}

/** The client's trailer-order confirmation as Sales has it (v2.2 P2). */
export interface TrailerOrderConfirmation {
  status: 'confirmed' | 'mismatch' | string;
  received_at: string | null;
  /** False when the order changed after it was confirmed: it needs confirming again. */
  matches_current: boolean;
}

export async function getTrailerOrderConfirmation(mode: PortalMode, rentalId: string): Promise<TrailerOrderConfirmation | null> {
  const res = await portalFetch(mode, bookingPath(rentalId, '/trailer-order'));
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { trailer_order?: TrailerOrderConfirmation | null };
  return body.trailer_order ?? null;
}

/**
 * "Yes, this is right". `orderKey` is the order on screen (portalFormat.orderKey): the server refuses
 * with 409 order_changed if the booking's order is different now. A share-link viewer confirms their
 * email first (verify_required), so Hollywood Trucks knows who confirmed.
 */
export async function confirmTrailerOrder(mode: PortalMode, rentalId: string, orderKey: string): Promise<TrailerOrderConfirmation> {
  const res = await portalFetch(mode, bookingPath(rentalId, '/trailer-order'), {
    method: 'POST',
    body: JSON.stringify({ status: 'confirmed', order_key: orderKey }),
  });
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { trailer_order: TrailerOrderConfirmation };
  return body.trailer_order;
}

/* ── Email confirmation for share-link viewers (T-195) ─────────────────────────────────────────
 * Viewing and vehicle documents need only the share link. Sending a change, confirming the order or
 * billing first asks the viewer to confirm they are a contact Hollywood Trucks lists for the
 * booking: a 6-digit code (or the Continue link) sent to that email. The answer to "send a code" is
 * the same whether or not the email is listed, so nothing here says which emails are. */

/** Thrown when an action needs a confirmed email first (403 verify_required). */
export const VERIFY_REQUIRED = 'verify_required';

export interface VerifyStatus {
  verified: boolean;
  /** "j***@studio.com" when this browser is confirmed. */
  verifiedAs: string | null;
  /** The share link's recipient, masked, to offer as the address to send to. */
  suggestedEmail: string | null;
}

export async function getVerifyStatus(mode: PortalMode): Promise<VerifyStatus> {
  const res = await portalFetch(mode, '/api/v1/portal/verify/status');
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { verified?: boolean; verified_as?: string | null; suggested_email?: string | null };
  return { verified: body.verified === true, verifiedAs: body.verified_as ?? null, suggestedEmail: body.suggested_email ?? null };
}

/**
 * Ask for a code for this booking. Without `email` it goes to the share link's recipient. The
 * answer never says whether the email is listed: `sentTo` is only the masked address asked for.
 */
export async function startVerification(mode: PortalMode, rentalId: string, email?: string): Promise<{ verificationId: string; sentTo: string; expiresIn: number }> {
  const res = await portalFetch(mode, '/api/v1/portal/verify/start', {
    method: 'POST',
    body: JSON.stringify({ rental_id: rentalId, ...(email ? { email } : {}) }),
  });
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { verification_id: string; sent_to: string; expires_in: number };
  return { verificationId: body.verification_id, sentTo: body.sent_to, expiresIn: body.expires_in };
}

/** The typed code. Refusals: code_incorrect (message says the tries left) or code_expired. */
export async function submitVerificationCode(mode: PortalMode, verificationId: string, code: string): Promise<{ verifiedAs: string }> {
  const res = await portalFetch(mode, '/api/v1/portal/verify/code', {
    method: 'POST',
    body: JSON.stringify({ verification_id: verificationId, code }),
  });
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { verified_as: string };
  return { verifiedAs: body.verified_as };
}

/** The emailed Continue link, on any device. → the booking it opens, when that link is still active. */
export async function redeemVerificationLink(token: string): Promise<{ verifiedAs: string; rentalId: string | null }> {
  const res = await linkFetch('/api/v1/portal/verify/token', { method: 'POST', body: JSON.stringify({ token }) });
  if (!res.ok) throw await readError(res);
  const body = await res.json() as { verified_as: string; rental_id?: string | null };
  return { verifiedAs: body.verified_as, rentalId: body.rental_id ?? null };
}

/** The Continue link's token, moved out of the address bar by index.html before any app code ran. */
type VerifyTokenWindow = Window & { __htPortalVerifyToken?: string };
export function readStashedVerifyToken(): string | null {
  return (window as VerifyTokenWindow).__htPortalVerifyToken ?? null;
}
export function clearStashedVerifyToken(): void {
  delete (window as VerifyTokenWindow).__htPortalVerifyToken;
}
